import {
    CheckGateFunction,
    LaunchpadTaskSchema,
    LaunchpadTaskValidator,
    LaunchpadValidationContext,
} from 'modules/common/components/Layout/LaunchpadTasks/useLaunchpadSchema/interfaces';
import {
    LaunchpadSubtaskType,
    LaunchpadFacadeSubtask,
    LaunchpadItem,
    TaskStatus,
} from 'modules/common/components/LaunchpadProgress/LaunchpadProgress.interface';
import { Member } from 'modules/common/interfaces/member';
import { OnboardingTaskTemplateName } from 'modules/HubV2/OnboardingV2/constants';
import { BusinessVerificationTaskName } from 'modules/Onboarding/BusinessVerificationFlow/constants';
import { GateEvaluation } from 'modules/common/useStatsigGate';

// Task IDs used in validators
const COMPLETE_ACCOUNTING_SETUP_ID = 'complete_accounting_setup';
const COMPLETE_PAYROLL_SETUP_ID = 'complete_payroll_setup';
const OPEN_CONNECT_BUSINESS_BANK_ACCOUNT_ID = 'open_connect_business_bank_account';
const INSURANCE_SETUP_ID = 'insurance_setup';

/**
 * Validators for top-level tasks.
 * Each validator determines if a task should be included in the schema.
 */
const TASK_VALIDATORS: Record<string, LaunchpadTaskValidator> = {
    [COMPLETE_ACCOUNTING_SETUP_ID]: ({ allTasksData }) => {
        const accountingSetupTaskExists = allTasksData[COMPLETE_ACCOUNTING_SETUP_ID] !== undefined;
        const isBankAccountCompleted =
            allTasksData[OPEN_CONNECT_BUSINESS_BANK_ACCOUNT_ID]?.status === TaskStatus.COMPLETED;
        // Only show accounting setup if:
        // 1. Bank account is not yet completed, OR
        // 2. "Complete accounting setup" task already exists in backend
        return !isBankAccountCompleted || accountingSetupTaskExists;
    },
    [COMPLETE_PAYROLL_SETUP_ID]: ({ allTasksData }) => {
        const payrollSetupTaskExists = allTasksData[COMPLETE_PAYROLL_SETUP_ID] !== undefined;
        const isBankAccountCompleted =
            allTasksData[OPEN_CONNECT_BUSINESS_BANK_ACCOUNT_ID]?.status === TaskStatus.COMPLETED;
        // Only show payroll setup if:
        // 1. Bank account is not yet completed, OR
        // 2. "Complete payroll setup" task already exists in backend
        return !isBankAccountCompleted || payrollSetupTaskExists;
    },
    // The backend decides enrollment once, when it creates the task (coverdash_launchpad is checked
    // in create_insurance_setup_task). The task's existence IS that decision, so the card follows it
    // rather than re-deriving eligibility here. That keeps enrollment sticky: nothing the member does
    // later — advancing stage, completing onboarding — can revoke a rollout they are already in.
    // The cost is no "coming soon" blocked card before enrollment, which is correct while only a
    // fraction of members are enrolled; a locked card is a promise we might not keep.
    // CANCELLED still counts as absent: a usertype change or offboarding cancels the task, and a
    // cancelled enrollment is not an enrollment.
    [INSURANCE_SETUP_ID]: ({ allTasksData }) => {
        const insuranceSetupTask = allTasksData[INSURANCE_SETUP_ID];
        return insuranceSetupTask !== undefined && insuranceSetupTask.status !== TaskStatus.CANCELLED;
    },
};

/**
 * Factory function for creating business verification subtask validators.
 * Determines if a verification subtask should be shown based on upload task completion and provider context.
 */
const validateBusinessVerificationTask =
    (
        taskName: (typeof BusinessVerificationTaskName)[keyof typeof BusinessVerificationTaskName],
        verifyTaskName: (typeof OnboardingTaskTemplateName)[keyof typeof OnboardingTaskTemplateName]
    ) =>
    ({ allTasksData }: LaunchpadValidationContext) => {
        const uploadTask = allTasksData[taskName];
        const verifyTask = allTasksData[verifyTaskName];

        // If upload task is not completed, don't show verify subtask
        if (uploadTask?.status !== TaskStatus.COMPLETED) {
            return false;
        }

        // If the verify task exists in backend, display it
        if (verifyTask?.status) {
            return true;
        }

        const context = uploadTask?.context;
        const provider = context?.completion_payload?.provider;

        return !provider;
    };

/**
 * Validators for subtasks (both regular subtasks and facade subtasks).
 * Each validator determines if a subtask should be included in the schema.
 */
const SUBTASK_VALIDATORS: Record<string, LaunchpadTaskValidator> = {
    [OnboardingTaskTemplateName.VERIFY_EIN_CONFIRMATION_LETTER]: validateBusinessVerificationTask(
        BusinessVerificationTaskName.BUSINESS_VERIFICATION_UPLOAD_EIN,
        OnboardingTaskTemplateName.VERIFY_EIN_CONFIRMATION_LETTER
    ),
    [OnboardingTaskTemplateName.VERIFY_SCORP_APPROVAL_LETTER]: validateBusinessVerificationTask(
        BusinessVerificationTaskName.BUSINESS_VERIFICATION_UPLOAD_S_CORP_CONFIRMATION_LETTER,
        OnboardingTaskTemplateName.VERIFY_SCORP_APPROVAL_LETTER
    ),
    [BusinessVerificationTaskName.VERIFY_BUSINESS]: ({ allTasksData }) => {
        const verifyBusinessTask = allTasksData[BusinessVerificationTaskName.VERIFY_BUSINESS];
        if (!verifyBusinessTask) {
            return false;
        }
        return true;
    },
};

/**
 * Helper to get the ID from a subtask (works for both regular subtasks and facade subtasks)
 */
function getSubtaskId(subtask: LaunchpadSubtaskType | LaunchpadFacadeSubtask): string | undefined {
    if ('id' in subtask) {
        return subtask.id;
    }
    if ('visual_dependencies' in subtask && subtask.visual_dependencies.length > 0) {
        return subtask.visual_dependencies[0];
    }
    return undefined;
}

/**
 * Helper to check if a feature gate is active
 * @param checkGate - Function to check gate status
 * @param featureGate - Gate evaluation or array of gate evaluations
 * @returns true if all gates match their expected values, false otherwise
 */
function checkIsGateActive(checkGate: CheckGateFunction, featureGate: GateEvaluation | GateEvaluation[]): boolean {
    if (Array.isArray(featureGate)) {
        return featureGate.every((gate) => {
            const isGateActive = checkGate(gate.name) ?? false;
            return isGateActive === gate.value;
        });
    }
    const isGateActive = checkGate(featureGate.name) ?? false;
    return isGateActive === featureGate.value;
}

/**
 * Applies validators to filter tasks and subtasks from the schema.
 * @param schema - The base schema to filter
 * @param allTasksData - All tasks data from the API
 * @param member - Current member data
 * @param checkGate - Function to check feature gate status
 * @returns Filtered schema with only tasks/subtasks that pass validation
 */
export function applySchemaValidators(
    schema: LaunchpadTaskSchema[],
    allTasksData: Record<string, Partial<LaunchpadItem>>,
    member: Member | undefined,
    checkGate: CheckGateFunction
): LaunchpadTaskSchema[] {
    return schema
        .filter((task) => {
            // Check feature gate first
            if (task.featureGate && !checkIsGateActive(checkGate, task.featureGate)) {
                return false;
            }

            const validator = TASK_VALIDATORS[task.id];
            if (!validator) {
                return true;
            }
            return validator({
                taskId: task.id,
                taskData: allTasksData[task.id],
                allTasksData,
                member,
            });
        })
        .map((task) => ({
            ...task,
            subtasks: task.subtasks.filter((subtask) => {
                // Check feature gate first for subtasks
                if (subtask.featureGate && !checkIsGateActive(checkGate, subtask.featureGate)) {
                    return false;
                }

                const subtaskId = getSubtaskId(subtask);
                if (!subtaskId) {
                    return true;
                }

                const validator = SUBTASK_VALIDATORS[subtaskId];
                if (!validator) {
                    return true;
                }
                return validator({
                    taskId: subtaskId,
                    taskData: allTasksData[subtaskId],
                    allTasksData,
                    member,
                });
            }),
        }));
}
